import React, { useState, useEffect, useContext } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; 
import DOMPurify from 'dompurify';

// Section for Utilities and Context
import "./postcard.css";
import API from "../utils/API";
import { AuthContext } from "../utils/AuthContext";

// Section for imported components 
import PostComment from "./PostComment";

// PostCardBody component
const PostCardBody = ({ body }) => {
    const MAX_CHARACTERS = 300;

    // Truncate body text while preserving HTML
    const truncateHtml = (html, maxLength) => {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        const plainText = tempDiv.textContent || tempDiv.innerText || "";
        
        if (plainText.length <= maxLength) return html;

        const truncatedText = plainText.substring(0, maxLength) + "...";
        tempDiv.textContent = truncatedText;
        return tempDiv.innerHTML;
    };

    const sanitizedBody = DOMPurify.sanitize(body, {
        ADD_ATTR: ['target', 'rel'],
    });

    const truncatedBody = truncateHtml(sanitizedBody, MAX_CHARACTERS);

    return (
        <div 
            className="card-text" 
            dangerouslySetInnerHTML={{ __html: truncatedBody }}
        />
    );
};


export default function PostCard(props) {
    const { auth } = useContext(AuthContext);
    const navigate = useNavigate();
    const [postTitle, setPostTitle] = useState("");
    const [postBody, setPostBody] = useState("");
    const [likedPost, setLikedPost] = useState(false);
    const [likeCount, setLikeCount] = useState();
    const [eventType, setEventType] = useState([]);
    const [singleDivision, setSingleDivision] = useState([]);
    const [singleOrganization, setSingleOrganization] = useState([]);
    const [, setExistingLike] = useState(false);
    const [readCount, setReadCount] = useState(props.reads);
    const [postEditing, setPostEditing] = useState(false);
    const [commentInput, setCommentInput] = useState("");
    const [commentCount, setCommentCount] = useState();
    const [eventLocationEntry, setEventLocationEntry] = useState("");
    const [eventDateEntry, setEventDateEntry] = useState(undefined);
    const [startTimeEntry, setStartTimeEntry] = useState(undefined);
    const [endTimeEntry, setEndTimeEntry] = useState(undefined);

    const handleNavigate = (profile_id) => {
        navigate("/profile/" + profile_id);
    }

    // Initialize like state from the parent-fetched userLikes array.
    // Runs once when userLikes loads, not on every parent re-render.
    useEffect(() => {
        if (!props.userLikes?.length) return;
        setLikedPost(props.userLikes.some(l => l.post_id === props.post_id && l.liked === true));
        setExistingLike(props.userLikes.some(l => l.post_id === props.post_id));
    }, [props.userLikes, props.post_id]);

    // Fetch per-post lookup data. Deps are specific primitives so this only
    // re-runs when the actual post data changes, not on every parent re-render.
    useEffect(() => {
        const fetchEventType = async () => {
            if (!props.event_type) return;
            try {
                const data = await API.getSingleEventType(props.event_type);
                setEventType(data.data);
            } catch (error) {
                console.log("Error fetching event type:", error);
            }
        };

        const fetchDivisionName = async () => {
            if (!props.div_id) return;
            try {
                const data = await API.getSingleDivisionName(props.div_id);
                setSingleDivision(data.data);
            } catch (error) {
                console.log("Error fetching division name:", error);
            }
        };

        const fetchOrganizationName = async () => {
            if (!props.org_id) return;
            try {
                const data = await API.getSingleOrganizationName(props.org_id);
                setSingleOrganization(data.data);
            } catch (error) {
                console.log("Error fetching organization name:", error);
            }
        };

        setCommentCount(props.comments ? props.comments.length : 0);
        setLikeCount(props.likes);
        setPostTitle(props.title);
        setPostBody(props.body);
        setEventLocationEntry(props.event_location);
        setEventDateEntry(props.event_date);
        setStartTimeEntry(props.event_start || "");
        setEndTimeEntry(props.event_end || "");

        fetchEventType();
        fetchDivisionName();
        fetchOrganizationName();

    }, [props.post_id, props.div_id, props.org_id, props.event_type,
        props.title, props.body, props.likes, props.comments,
        props.event_location, props.event_date, props.event_start, props.event_end]);

    const handlePostDate = (datetime) => {
        const date = new Date(datetime);
    
        // Adjust for timezone offset
        date.setMinutes(date.getMinutes() + date.getTimezoneOffset());

        return date.toDateString();
    };

    const handlePostTime = (time) => {
        // Split the time string into hours, minutes, and seconds
        let [hours, minutes] = time.split(':');

        // Convert hours from string to number
        hours = parseInt(hours, 10);

        // Determine the AM/PM period
        const period = hours >= 12 ? 'PM' : 'AM';

        // Convert hours to 12-hour format
        hours = hours % 12 || 12; // Convert '0' to '12' for midnight

        // Return the formatted time
        return `${hours}:${minutes} ${period}`;
    };

    const handleLike = async () => {
        // NEED TO CALL THE LOGIN COMPONENT HERE !!! <<<<<<<<<
        if (!auth.user) {
            console.log("Login required to like posts.");
            return;
        }

        try {
            // Calculate the new like count
            const newLikeCount = likedPost ? likeCount - 1 : likeCount + 1;
    
            // Update the state
            setLikeCount(newLikeCount);
            setLikedPost(prevLikedPost => !prevLikedPost);
    
            const data = {
                post_id: props.post_id,
                likes_count: newLikeCount,
                liked: !likedPost,
                user_id: auth.user?.user_id,
            };
    
            await API.addNewLike(data);
        } catch (error) {
            console.error("Server request failed: ", error);
            setLikeCount(likedPost ? likeCount + 1 : likeCount - 1);
            setLikedPost(prevLikedPost => !prevLikedPost);
        }
    };

    const handleComment = async () => {
        // NEED TO CALL THE LOGIN COMPONENT HERE !!! <<<<<<<<<
        if (!auth.user) {
            console.log("Login required to comment.");
            return;
        }


        try {

            if (commentInput !== "") {
                const data = {
                    post_id: props.post_id,
                    comment_author: auth.user?.profile_id,
                    comment_body: commentInput,
                    user_id: auth.user?.user_id
                }

                await API.addNewComment(data);
                props.refresh();
                setCommentInput("");
            } else {
                console.log("No comment provided")
                return;
            }           
            
        } catch(error) {
            console.error("Server request failed: ",error);
        }
    }

    const updatePost = async () => {
        try {
            const data = {
                post_id: props.post_id,
                post_title: postTitle,
                post_body: postBody
            }

            if (props.postTypeName === "EVENT") {
                data.event_date = eventDateEntry;
                data.event_start = startTimeEntry;
                data.event_end = endTimeEntry;
            }

            await API.updatePost(data);
            props.refresh();

        } catch(error) {
            console.error("Server request failed: ",error);
        }
    };

    const handleEditPost = () => {
        setPostEditing(prevEditPost => !prevEditPost);

        if (postEditing) {
            updatePost();
        }
    };
    
    const addRead = async () => {
        const newReadCount = readCount + 1;
        setReadCount(newReadCount);

        try {
            const data = {
                post_id: props.post_id,
                reads: newReadCount,
            } 

            await API.updateReadCount(data);

        } catch(error) {
            console.error("Server request failed: ",error);
        }
    };

    // HANDLER FOR DELETING A USER'S POST
    const deletePost = async () => {
        try {
            const data = {
                post_id: props.post_id
            }
            
            await API.deletePost(data);
            props.refresh();

        } catch(error) {
           console.error("Server request failed: ",error);
        }        
    };

    return (
        <>
            <div className="card post-card mb-3" key={props.post_id}>
                <div className="card-header post-card-header" >
                    <h6 className="post-type mt-2 pe-2">{props.postTypeName}{props.postTypeName === "EVENT" && (` - ${eventType.event_type}`)}</h6>
                    <h5 className="post-title pt-1 display-6 px-2">{postTitle}</h5>
                    <p className="post-byline mb-0 px-2"><a className="post-author" onClick={() => handleNavigate(props.author_id)}>{props.author}</a> | <i>{props.div_id ? singleDivision?.division_name : singleOrganization.organization_name}</i> | {handlePostDate(props.postDate)}</p>
                </div>
                <div className="card-body pt-2 px-4">
                    <PostCardBody body={postBody} />
                    {props.compact ? (
                        <div className="d-flex justify-content-end mt-3">
                            <button type="button" className="btn read-more-button read-more-button-static" data-bs-toggle="modal" data-bs-target={`#readPostModal` + props.post_id} onClick={addRead}><i className="bi bi-book"> </i>Keep Reading</button>
                        </div>
                    ) : (
                        <div className="row mb-0 mt-3">
                            <div className="col-7">
                                <div className="post-likes ms-2">
                                    <button
                                        className={`bi post-likes-heart ${likedPost === true ? 'bi-heart-fill' : 'bi-heart'}`}
                                        onClick={auth.user ? handleLike : null}
                                        disabled={!auth.user}
                                    /> {likeCount}
                                    <span
                                        className={`bi ms-3 comment-count bi-chat-text`}
                                    /> {commentCount}
                                </div>
                            </div>
                            <div className="col-4">
                                <button type="button" className="btn read-more-button " data-bs-toggle="modal" data-bs-target={`#readPostModal` + props.post_id} onClick={addRead}><i className="bi bi-book"> </i>Keep Reading</button>
                            </div>
                        </div>
                    )}
                </div>

                {!props.compact && (
                    <div className="card-footer my-0 pt-0">
                        <div className="input-group input-group-sm mb-3 post-comment-div">
                            <input type="text"
                                className="form-control rounded-end post-add-comment ms-2"
                                aria-label="Sizing example input"
                                aria-describedby="inputGroup-sizing-sm"
                                placeholder="Add comment..."
                                value={commentInput}
                                disabled={!auth.user}
                                onChange={(e) => setCommentInput(e.target.value)}
                            />
                            <span><i className="bi bi-plus-circle submit-comment-icon my-2 ms-3" onClick={handleComment}></i></span>
                        </div>
                    </div>
                )}
            </div>


            {/* Modal to read a full post. Portaled to document.body so it
                isn't trapped inside an ancestor's stacking context (e.g.
                Profile's position:sticky column), which otherwise renders
                it behind the navbar/backdrop — same root cause as the old
                Edit Job modal bug. */}
            {createPortal(
            <div className="modal fade read-post-modal" key={"post" + props.post_id} id={`readPostModal`+ props.post_id} tabIndex="-1" aria-labelledby="readPostModalLabel" aria-hidden="true">
                <div className="modal-dialog">
                    <div className="modal-content post-modal-content position-absolute top-50 start-50 translate-middle-x">
                        <div className="modal-header post-modal-header">
                            {!postEditing && (
                                <h5 className="modal-title post-modal-title display-6">{postTitle}</h5>
                            )}
                            {postEditing && (
                                <input 
                                    className="post-title-input form-control py-1" 
                                    value={postTitle}
                                    onChange={(e) => setPostTitle(e.target.value)}
                                ></input>
                                
                            )}
                            
                        </div>
                        <div className="modal-body">
                            <div className="row mb-0">
                                <div className="col-10">
                                    <p className="post-byline mb-0"><a className="post-author" data-bs-dismiss="modal" onClick={() => handleNavigate(props.author_id)}>{props.author}</a> | <i>{props.div_id ? singleDivision?.division_name : singleOrganization.organization_name}</i> | {handlePostDate(props.postDate)}</p>
                                </div>
                                <div className="col-2">
                                    <div className="post-likes">
                                        <span><button className={`bi post-likes-heart ${likedPost === true ? 'bi-heart-fill' : 'bi-heart'}`} 
                                         onClick={auth.user ? handleLike : null}
                                         disabled={!auth.user}></button> {likeCount} </span>
                                        {props.user_id === auth.user?.user_id && (
                                            <span><i className={`bi edit-post-button ms-2 ${postEditing === false ? 'bi-pencil-square ' : 'bi-floppy2-fill'}`} onClick={() => handleEditPost()}></i></span>
                                        )}
                                        
                                    </div>  
                                </div>
                            </div>
                            <br/>
                            {!postEditing && (
                                <>
                                <div 
                                    className="card-text post-body mb-4 pre-wrap"
                                    dangerouslySetInnerHTML={{ 
                                        __html: DOMPurify.sanitize(postBody, {
                                            ADD_ATTR: ['target', 'rel']
                                        }) 

                                    }}
                                />
                                {props.postTypeName === "EVENT" && (
                                    <>
                                        <span className="post-event-date">Location: {eventLocationEntry}</span><br/>
                                        <span className="post-event-date">Date: {handlePostDate(eventDateEntry)}</span><br/>
                                        <span className="post-event-date">Time: {handlePostTime(startTimeEntry)} to {handlePostTime(endTimeEntry)}</span>
                                    </>                                        
                                )}
                            </>                             

                            )}
                            {postEditing && (
                                <>                                    
                                    {/* <textarea 
                                        className="post-body-textarea form-control" 
                                        value={postBody}
                                        onChange={(e) => setPostBody(e.target.value)}
                                    ></textarea> */}
                                    <div>
                                        <ReactQuill
                                            className="form-control mb-2 p-1 post-body-input" 
                                            value={postBody}
                                            onChange={setPostBody}
                                            placeholder="Write your post..."
                                            modules={{
                                                toolbar: [
                                                    ['bold', 'italic', 'underline'],       // Text formatting
                                                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                                    ['link'],                             // Link for URL/email
                                                    ['clean']                             // Clear formatting
                                                ],
                                            }}
                                            formats={['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link']}
                                        />
                                    </div>
                                    {props.postTypeName === "EVENT" && (
                                        <>
                                            <div className="row mt-3">
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text post-event">Location: </span>
                                                    <input 
                                                        type="text" 
                                                        className="form-control  edit-event-location" 
                                                        aria-label=""  
                                                        value={eventLocationEntry}
                                                        onChange={(e) => {setEventLocationEntry(e.target.value)}}
                                                        required
                                                    />
                                                
                                                </div>
                                            </div>
                                            <div className="row mt-3">
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text post-event">Date: </span>
                                                    <input 
                                                        type="date" 
                                                        className="form-control  edit-event-date" 
                                                        aria-label=""  
                                                        value={eventDateEntry}
                                                        onChange={(e) => {setEventDateEntry(e.target.value)}}
                                                        required
                                                    />
                                                </div>

                                            </div>
                                            <div className="row mt-3">
                                                <div className="col-4">
                                                    <div className="input-group input-group-sm">
                                                        <span className="input-group-text post-event">From: </span>
                                                        <input 
                                                            type="time" 
                                                            className="form-control edit-event-time" 
                                                            aria-label=""
                                                            value={startTimeEntry}
                                                            onChange={(e) => {setStartTimeEntry(e.target.value)}}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-4">
                                                    <div className="input-group input-group-sm">
                                                        <span className="input-group-text post-event">Until: </span>
                                                        <input 
                                                        type="time" 
                                                        className="form-control edit-event-time" 
                                                        aria-label=""  
                                                        value={endTimeEntry}
                                                        onChange={(e) => {setEndTimeEntry(e.target.value)}}
                                                        required
                                                        />
                                                    </div>
                                                </div>
                                                
                                            </div>
                                        </>                                        
                                    )}                                    
                                </>
                            )}
                            <div ><hr/></div>
                                <div className="input-group input-group-sm mb-3 post-comment-div">
                                    <input type="text" 
                                        className="form-control rounded-end post-add-comment ms-2" 
                                        aria-label="Sizing example input" 
                                        aria-describedby="inputGroup-sizing-sm" 
                                        placeholder="Add comment..." 
                                        value={commentInput}
                                        disabled={!auth.user}
                                        onChange={(e) => setCommentInput(e.target.value)}
                                    />
                                    <span><i className="bi bi-plus-circle submit-comment-icon my-2 ms-3 me-2" onClick={handleComment}></i></span>
                                </div>
                            {props.comments
                            .filter(comment => comment.post_id === props.post_id)
                            .map((comment, index) => (                                
                                <PostComment 
                                    comment_id={comment.comment_id}
                                    profile_id={comment.ProfileInfo.profile_id}
                                    first_name={comment.ProfileInfo.first_name}
                                    last_name={comment.ProfileInfo.last_name}
                                    comment_body={comment.comment_body}
                                    user_id={comment.user_id}
                                    createdAt={comment.createdAt}
                                    updatedAt={comment.updatedAt}   
                                    refresh={props.refresh}                                 
                                />
                            ))}
                            

                        </div>
                        <div className="modal-footer pt-0">
                            {props.user_id === auth.user?.user_id && (
                                <span className="post-delete" data-bs-dismiss="modal" 
                                    onClick={deletePost}
                                ><i className="bi bi-trash3"/>
                                </span>
                            )}
                            <button type="button" className="btn modal-close" data-bs-dismiss="modal" alt="Button to close modal">Close</button>                            
                        </div>
                    </div>
                </div>
            </div>,
            document.body
            )}
        </>
    )
}